import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { authAPI } from '@/lib/api';
import { 
  LayoutDashboard, GitFork, ClipboardList, PackageCheck, FileCheck,
  LogOut, Menu, ChevronRight, Key, Eye, EyeOff, Home, BarChart3, FileSearch
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '@/components/ui/dialog';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import { PageBreadcrumb } from '@/components/PageBreadcrumb';

const subcontractNavItems = [
  { path: '/subcontract', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/subcontract/challans', icon: GitFork, label: 'Challans' },
  { path: '/subcontract/job-tracker', icon: ClipboardList, label: 'Job Work Tracker' },
  { path: '/subcontract/traceability-report', icon: FileSearch, label: 'Traceability Report' },
];

export default function SubcontractLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [changePasswordModal, setChangePasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }
    if (newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    try {
      await authAPI.changePassword({ current_password: currentPassword, new_password: newPassword });
      toast.success('Password changed');
      setChangePasswordModal(false);
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to change password'); }
  };

  return (
    <div className="min-h-screen bg-background flex">
      <Toaster richColors position="top-right" />
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-40 w-56 bg-slate-900 text-slate-100 transform transition-transform duration-200 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 lg:static lg:inset-auto`}>
        <div className="flex items-center gap-2 px-4 h-14 border-b border-slate-700/50">
          <GitFork className="w-5 h-5 text-violet-400" />
          <span className="font-semibold text-sm tracking-tight">Subcontracting</span>
        </div>
        <nav className="flex-1 px-2 py-3 space-y-0.5 overflow-y-auto">
          {subcontractNavItems.map(item => (
            <NavLink key={item.path} to={item.path} end={item.path === '/subcontract'}
              className={({ isActive }) => `flex items-center gap-2.5 px-3 py-2 rounded-md text-xs font-medium transition-colors ${isActive ? 'bg-violet-600/20 text-violet-300 border-l-2 border-violet-400' : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'}`}
              onClick={() => setSidebarOpen(false)}>
              <item.icon className="w-4 h-4 flex-shrink-0" />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="px-2 py-3 border-t border-slate-700/50 space-y-0.5">
          <button onClick={() => navigate('/')} className="flex items-center gap-2.5 px-3 py-2 rounded-md text-xs font-medium text-slate-400 hover:text-slate-200 hover:bg-slate-800/50 w-full">
            <Home className="w-4 h-4" /> Home
          </button>
        </div>
      </aside>
      {sidebarOpen && <div className="fixed inset-0 z-30 bg-black/30 lg:hidden" onClick={() => setSidebarOpen(false)} />}
      {/* Main */}
      <div className="flex-1 flex flex-col min-h-screen">
        <header className="h-14 border-b bg-background/95 backdrop-blur flex items-center justify-between px-4 sticky top-0 z-20">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" className="lg:hidden" onClick={() => setSidebarOpen(true)}>
              <Menu className="w-4 h-4" />
            </Button>
            <PageBreadcrumb />
          </div>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2 text-xs">
                  <div className="w-6 h-6 rounded-full bg-violet-600 flex items-center justify-center text-white text-[10px] font-bold">
                    {user?.name?.[0]?.toUpperCase() || 'U'}
                  </div>
                  <span className="hidden sm:inline">{user?.name}</span>
                  <ChevronRight className="w-3 h-3 rotate-90" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setChangePasswordModal(true)} className="text-xs"><Key className="w-3 h-3 mr-2" /> Change Password</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} className="text-xs text-red-600"><LogOut className="w-3 h-3 mr-2" /> Logout</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="flex-1 p-4 lg:p-6 overflow-y-auto">
          <Outlet />
        </main>
      </div>
      {/* Change Password Dialog */}
      <Dialog open={changePasswordModal} onOpenChange={setChangePasswordModal}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle className="text-base">Change Password</DialogTitle><DialogDescription className="text-xs">Enter current and new password</DialogDescription></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label className="text-xs">Current Password</Label><div className="relative"><Input type={showCurrentPassword ? 'text' : 'password'} value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} className="h-8 text-xs pr-8" /><button type="button" onClick={() => setShowCurrentPassword(!showCurrentPassword)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground">{showCurrentPassword ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}</button></div></div>
            <div><Label className="text-xs">New Password</Label><div className="relative"><Input type={showNewPassword ? 'text' : 'password'} value={newPassword} onChange={e => setNewPassword(e.target.value)} className="h-8 text-xs pr-8" /><button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground">{showNewPassword ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}</button></div></div>
            <div><Label className="text-xs">Confirm New Password</Label><Input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="h-8 text-xs" /></div>
          </div>
          <DialogFooter><Button onClick={handleChangePassword} className="h-8 text-xs w-full">Update Password</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
